/**
 * ShiftPlanning — Dense staffing grid showing positions × dates.
 *
 * Rows: Grouped by station → unit → seat (excluding utilities)
 * Columns: Infinite horizontal scroll, loading month-by-month
 * All authenticated users can view; officers+ can assign members to vacant seats.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isOfficerOrAbove } from '../lib/auth';
import { useShiftPlanning, type AssignParams } from '../hooks/useShiftPlanning';
import { useMemberPhotos } from '../hooks/useMemberPhotos';
import { apiFetch } from '../lib/api';
import AppLayout from '../components/layout/AppLayout';
import { ALL_STATIONS, BC_BUGGY_SEATS, SAFETY_SEATS, type UnitDef } from '@shared/roster-config';
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  RefreshCw,
  Search,
  X,
  UserPlus,
  AlertTriangle,
  Loader2,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

// ── Station display names ────────────────────────────────────────────────

const STATION_DISPLAY_NAMES: Record<string, string> = {
  '510': 'FS10 - Dime House',
  '513': 'FS13 - Midtown',
  '518': 'FS18 - Retirement Home',
  '520': 'FS20 - Parkway Express',
  CMD: 'Command',
};

// ── Battalion colors ─────────────────────────────────────────────────────

const BN_COLORS: Record<number, { text: string; bg: string; border: string }> = {
  1: { text: '#4DD0E1', bg: 'rgba(0, 188, 212, 0.10)', border: 'rgba(0, 188, 212, 0.25)' },
  2: { text: '#AED581', bg: 'rgba(76, 175, 80, 0.10)', border: 'rgba(76, 175, 80, 0.25)' },
  3: { text: '#EF9A9A', bg: 'rgba(244, 67, 54, 0.10)', border: 'rgba(244, 67, 54, 0.25)' },
  4: { text: '#FFB74D', bg: 'rgba(255, 152, 0, 0.10)', border: 'rgba(255, 152, 0, 0.25)' },
  5: { text: '#9FA8DA', bg: 'rgba(63, 81, 181, 0.10)', border: 'rgba(63, 81, 181, 0.25)' },
};

// ── Unit type filter options ────────────────────────────────────────────

const UNIT_TYPE_OPTIONS = [
  { value: '', label: 'All Units' },
  { value: 'engine', label: 'Engine' },
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'medic', label: 'Medic' },
  { value: 'tower', label: 'Tower' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'brush', label: 'Brush' },
  { value: 'buggy', label: 'BC Buggy' },
  { value: 'safety', label: 'Safety' },
];

// ── Row structure ────────────────────────────────────────────────────────

interface GridRow {
  type: 'station' | 'unit' | 'seat';
  stationId: string;
  stationLabel: string;
  unitId?: string;
  unitLabel?: string;
  unitType?: string;
  seatId?: string;
  seatLabel?: string;
  required?: boolean;
}

function buildGridRows(unitTypeFilter: string): GridRow[] {
  const rows: GridRow[] = [];

  // Station units (excluding utility)
  for (const station of ALL_STATIONS) {
    const filteredUnits = station.units.filter(
      (u) => u.type !== 'utility' && (unitTypeFilter === '' || u.type === unitTypeFilter),
    );
    if (filteredUnits.length === 0) continue;

    rows.push({
      type: 'station',
      stationId: station.id,
      stationLabel: STATION_DISPLAY_NAMES[station.id] ?? station.label,
    });

    for (const unit of filteredUnits) {
      rows.push({
        type: 'unit',
        stationId: station.id,
        stationLabel: station.label,
        unitId: unit.id,
        unitLabel: unit.label,
        unitType: unit.type,
      });

      for (const seat of unit.seats) {
        rows.push({
          type: 'seat',
          stationId: station.id,
          stationLabel: station.label,
          unitId: unit.id,
          unitLabel: unit.label,
          unitType: unit.type,
          seatId: seat.id,
          seatLabel: seat.label,
          required: seat.required,
        });
      }
    }
  }

  // Command units
  if (unitTypeFilter === '' || unitTypeFilter === 'buggy' || unitTypeFilter === 'safety') {
    const commandUnits: UnitDef[] = [];
    if (unitTypeFilter === '' || unitTypeFilter === 'buggy') {
      commandUnits.push({ id: 'BC', label: 'BC Buggy', type: 'buggy', seats: BC_BUGGY_SEATS });
    }
    if (unitTypeFilter === '' || unitTypeFilter === 'safety') {
      commandUnits.push({ id: 'SAF', label: 'Safety', type: 'safety', seats: SAFETY_SEATS });
    }

    if (commandUnits.length > 0) {
      rows.push({
        type: 'station',
        stationId: 'CMD',
        stationLabel: STATION_DISPLAY_NAMES['CMD'],
      });

      for (const unit of commandUnits) {
        rows.push({
          type: 'unit',
          stationId: 'CMD',
          stationLabel: 'Command',
          unitId: unit.id,
          unitLabel: unit.label,
          unitType: unit.type,
        });
        for (const seat of unit.seats) {
          rows.push({
            type: 'seat',
            stationId: 'CMD',
            stationLabel: 'Command',
            unitId: unit.id,
            unitLabel: unit.label,
            unitType: unit.type,
            seatId: seat.id,
            seatLabel: seat.label,
            required: seat.required,
          });
        }
      }
    }
  }

  return rows;
}

/** Resolve the actual unit_id for command units based on battalion number */
function resolveUnitId(row: GridRow, bnNum: number): string {
  if (row.unitId === 'BC') return `BC${bnNum}`;
  if (row.unitId === 'SAF') return `SAF${bnNum}`;
  return row.unitId ?? '';
}

// ── Date formatting ──────────────────────────────────────────────────────

function formatColDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDate();
  const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  return `${day} ${month}`;
}

function formatDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
}

function getLocalToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function isToday(dateStr: string): boolean {
  return dateStr === getLocalToday();
}

// ── Eligible member type (for assign modal) ──────────────────────────────

interface EligibleMember {
  id: number;
  display_name: string;
  email: string;
  battalion_id: number | null;
  battalion_number: number | null;
  already_assigned: boolean;
}

// ── Assign Modal ─────────────────────────────────────────────────────────

interface AssignModalProps {
  date: string;
  battalionId: number;
  unitId: string;
  seatId: string;
  seatLabel: string;
  onAssign: (params: AssignParams) => Promise<void>;
  onClose: () => void;
}

function AssignModal({ date, battalionId, unitId, seatId, seatLabel, onAssign, onClose }: AssignModalProps) {
  const [members, setMembers] = useState<EligibleMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiFetch<{ eligible_members: EligibleMember[] }>(`/api/officer/eligible/${unitId}/${seatId}`)
      .then((data) => setMembers(data.eligible_members))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [unitId, seatId]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!search) return members;
    const q = search.toLowerCase();
    return members.filter((m) => m.display_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [members, search]);

  const handleAssign = useCallback(
    async (memberId: number) => {
      setAssigning(true);
      try {
        await onAssign({ date, battalion_id: battalionId, unit_id: unitId, seat_id: seatId, member_id: memberId });
        onClose();
      } catch {
        setAssigning(false);
      }
    },
    [date, battalionId, unitId, seatId, onAssign, onClose],
  );

  const formattedDate = formatColDate(date);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-xl border border-edge-default bg-surface-raised shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-edge-muted px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-content-primary">Assign Member</h3>
            <p className="mt-0.5 text-sm text-content-secondary">
              {unitId} &middot; {seatLabel} &middot; {formattedDate}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="border-b border-edge-muted px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg bg-surface-base px-3 py-2">
            <Search size={16} className="text-content-tertiary" />
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-sm text-content-primary placeholder:text-content-disabled focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Member list */}
        <div className="max-h-80 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-content-tertiary" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-content-tertiary">No eligible members found</p>
          ) : (
            filtered.map((m) => (
              <button
                key={m.id}
                onClick={() => handleAssign(m.id)}
                disabled={assigning}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-surface-sunken disabled:opacity-50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-medium text-content-primary">
                  {m.display_name
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-content-primary">{m.display_name}</div>
                  <div className="truncate text-xs text-content-tertiary">
                    {m.battalion_number ? `B${m.battalion_number}` : 'Unassigned'}
                    {m.already_assigned ? ' \u00b7 Has seat' : ''}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Scroll threshold for loading more ────────────────────────────────────
const SCROLL_THRESHOLD = 300;
const COL_SCROLL_PX = 480; // ~5 columns worth of scroll per arrow click

// ── Main Component ───────────────────────────────────────────────────────

export default function ShiftPlanning() {
  const { user } = useAuth();
  const isOfficer = isOfficerOrAbove(user);
  const {
    dates,
    loading,
    loadingFuture,
    loadingPast,
    error,
    battalionFilter,
    setBattalionFilter,
    loadMore,
    hasMoreFuture,
    hasMorePast,
    assignMember,
    refresh,
    loadMonth,
  } = useShiftPlanning();

  const [unitTypeFilter, setUnitTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedStations, setCollapsedStations] = useState<Set<string>>(new Set(['518', '520']));
  const [collapsedUnits, setCollapsedUnits] = useState<Set<string>>(new Set(['510:R510', '513:M513']));
  const [assignTarget, setAssignTarget] = useState<{
    date: string;
    battalionId: number;
    unitId: string;
    seatId: string;
    seatLabel: string;
  } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const hasScrolledToToday = useRef(false);
  const colRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll indicator state
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Build grid rows based on filters
  const gridRows = useMemo(() => buildGridRows(unitTypeFilter), [unitTypeFilter]);

  // Collect all member IDs for photos
  const memberIds = useMemo(() => {
    const ids = new Set<number>();
    for (const d of dates) {
      for (const a of d.assignments) {
        if (a.member_id) ids.add(a.member_id);
      }
    }
    return Array.from(ids);
  }, [dates]);

  const photos = useMemberPhotos(memberIds);

  // Filter rows by search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return gridRows;
    const q = searchQuery.toLowerCase();

    // Find matching seat rows, then include their parent station/unit headers
    const matchingSeatRows = new Set<number>();
    const matchingStations = new Set<string>();
    const matchingUnits = new Set<string>();

    gridRows.forEach((row, idx) => {
      if (row.type === 'seat') {
        // Check if any date has a member matching the search in this seat
        const seatMatches = dates.some((d) =>
          d.assignments.some(
            (a) =>
              a.unit_id === resolveUnitId(row, d.battalion_number) &&
              a.seat_id === row.seatId &&
              a.display_name?.toLowerCase().includes(q),
          ),
        );
        // Also match seat label or unit label
        if (seatMatches || row.seatLabel?.toLowerCase().includes(q) || row.unitLabel?.toLowerCase().includes(q)) {
          matchingSeatRows.add(idx);
          matchingStations.add(row.stationId);
          matchingUnits.add(`${row.stationId}:${row.unitId}`);
        }
      }
    });

    return gridRows.filter((row, idx) => {
      if (row.type === 'station') return matchingStations.has(row.stationId);
      if (row.type === 'unit') return matchingUnits.has(`${row.stationId}:${row.unitId}`);
      return matchingSeatRows.has(idx);
    });
  }, [gridRows, searchQuery, dates]);

  // Build a lookup for quick cell access: `date:unitId:seatId` → assignment
  const assignmentLookup = useMemo(() => {
    const map = new Map<string, { member_id: number | null; display_name: string | null; is_override: boolean }>();
    for (const d of dates) {
      for (const a of d.assignments) {
        map.set(`${d.date}:${a.unit_id}:${a.seat_id}`, {
          member_id: a.member_id,
          display_name: a.display_name,
          is_override: a.is_override,
        });
      }
    }
    return map;
  }, [dates]);

  // Coverage requests lookup: `date:unitId:seatId` → boolean
  const coverageLookup = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const d of dates) {
      for (const cr of d.coverage_requests) {
        if (cr.position_needed) {
          map.set(`${d.date}:${cr.position_needed}`, true);
        }
      }
    }
    return map;
  }, [dates]);

  // Compute column stats
  const columnStats = useMemo(() => {
    return dates.map((d) => {
      const required = d.assignments.filter((a) => a.is_required);
      const filled = required.filter((a) => a.member_id !== null).length;
      const total = required.length;
      const pct = total > 0 ? Math.round((filled / total) * 100) : 100;
      const unfilled = total - filled;
      return { pct, unfilled, filled, total };
    });
  }, [dates]);

  // Compute station-level stats across all dates
  const stationStats = useMemo(() => {
    const stats = new Map<string, { filled: number; unfilled: number }>();
    for (const d of dates) {
      for (const a of d.assignments) {
        if (!a.is_required) continue;
        const stationId = a.station_id;
        if (!stats.has(stationId)) stats.set(stationId, { filled: 0, unfilled: 0 });
        const s = stats.get(stationId)!;
        if (a.member_id !== null) s.filled++;
        else s.unfilled++;
      }
    }
    return stats;
  }, [dates]);

  // Per-date stats for stations: `date:stationId` → { filled, total }
  const stationDateStats = useMemo(() => {
    const map = new Map<string, { filled: number; total: number }>();
    for (const d of dates) {
      for (const a of d.assignments) {
        if (!a.is_required) continue;
        const key = `${d.date}:${a.station_id}`;
        if (!map.has(key)) map.set(key, { filled: 0, total: 0 });
        const s = map.get(key)!;
        s.total++;
        if (a.member_id !== null) s.filled++;
      }
    }
    return map;
  }, [dates]);

  // Per-date stats for units: `date:unitId` → { filled, total }
  const unitDateStats = useMemo(() => {
    const map = new Map<string, { filled: number; total: number }>();
    for (const d of dates) {
      for (const a of d.assignments) {
        if (!a.is_required) continue;
        const key = `${d.date}:${a.unit_id}`;
        if (!map.has(key)) map.set(key, { filled: 0, total: 0 });
        const s = map.get(key)!;
        s.total++;
        if (a.member_id !== null) s.filled++;
      }
    }
    return map;
  }, [dates]);

  const toggleStation = useCallback((stationId: string) => {
    setCollapsedStations((prev) => {
      const next = new Set(prev);
      if (next.has(stationId)) next.delete(stationId);
      else next.add(stationId);
      return next;
    });
  }, []);

  const toggleUnit = useCallback((unitKey: string) => {
    setCollapsedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitKey)) next.delete(unitKey);
      else next.add(unitKey);
      return next;
    });
  }, []);

  // Filter out collapsed rows
  const visibleRows = useMemo(() => {
    return filteredRows.filter((row) => {
      if (row.type === 'unit' || row.type === 'seat') {
        if (collapsedStations.has(row.stationId)) return false;
      }
      if (row.type === 'seat') {
        const unitKey = `${row.stationId}:${row.unitId}`;
        if (collapsedUnits.has(unitKey)) return false;
      }
      return true;
    });
  }, [filteredRows, collapsedStations, collapsedUnits]);

  const handleCellClick = useCallback(
    (date: string, battalionId: number, unitId: string, seatId: string, seatLabel: string) => {
      if (!isOfficer) return;
      setAssignTarget({ date, battalionId, unitId, seatId, seatLabel });
    },
    [isOfficer],
  );

  // Compute scroll position for a column relative to the scroll container
  // Uses getBoundingClientRect for reliability regardless of DOM nesting
  const getScrollLeftForCol = useCallback((colEl: HTMLElement): number => {
    const container = scrollRef.current;
    if (!container) return 0;
    const containerRect = container.getBoundingClientRect();
    const colRect = colEl.getBoundingClientRect();
    return container.scrollLeft + (colRect.left - containerRect.left);
  }, []);

  // Scroll to a specific date column
  const scrollToDate = useCallback(
    (dateStr: string, behavior: ScrollBehavior = 'smooth') => {
      const el = colRefs.current.get(dateStr);
      if (el && scrollRef.current) {
        scrollRef.current.scrollTo({ left: getScrollLeftForCol(el), behavior });
      }
    },
    [getScrollLeftForCol],
  );

  // Scroll to today's column
  const scrollToToday = useCallback(() => {
    const todayStr = getLocalToday();
    scrollToDate(todayStr);
  }, [scrollToDate]);

  // Scroll to Monday of the current week on initial data load
  useEffect(() => {
    if (!loading && dates.length > 0 && !hasScrolledToToday.current && scrollRef.current) {
      // Compute Monday of the current week
      const now = new Date();
      const dow = now.getDay(); // 0=Sun
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const monday = new Date(now);
      monday.setDate(monday.getDate() + mondayOffset);
      const mondayStr = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

      // Use double-rAF to ensure DOM is fully laid out after React commit
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          const el = colRefs.current.get(mondayStr);
          if (el && scrollRef.current) {
            scrollRef.current.scrollTo({ left: getScrollLeftForCol(el), behavior: 'instant' });
            hasScrolledToToday.current = true;
          }
        });
      });
      return () => {
        cancelled = true;
      };
    }
  }, [loading, dates, getScrollLeftForCol]);

  // Update scroll indicator state
  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollWidth - el.scrollLeft - el.clientWidth > 10);
  }, []);

  // Infinite scroll handler + scroll indicators
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    // Update scroll indicators
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollWidth - scrollLeft - clientWidth > 10);

    // Near right edge → load more future
    if (scrollWidth - scrollLeft - clientWidth < SCROLL_THRESHOLD && hasMoreFuture && !loadingFuture) {
      loadMore('future');
    }

    // Near left edge → load more past
    if (scrollLeft < SCROLL_THRESHOLD && hasMorePast && !loadingPast) {
      loadMore('past');
    }
  }, [hasMoreFuture, hasMorePast, loadingFuture, loadingPast, loadMore]);

  // Initialize scroll indicators after data loads
  useEffect(() => {
    if (!loading && dates.length > 0) {
      // Small delay to let the DOM render the columns
      const timer = setTimeout(updateScrollIndicators, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, dates.length, updateScrollIndicators]);

  const scrollByAmount = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'right' ? COL_SCROLL_PX : -COL_SCROLL_PX, behavior: 'smooth' });
  }, []);

  // Date navigation: jump by calendar days
  const jumpByDays = useCallback(
    async (days: number) => {
      if (dates.length === 0) return;
      const container = scrollRef.current;
      if (!container) return;

      // Find the currently visible date (leftmost visible column) using getBoundingClientRect
      const containerRect = container.getBoundingClientRect();
      let visibleIdx = 0;
      for (let i = 0; i < dates.length; i++) {
        const colEl = colRefs.current.get(dates[i].date);
        if (colEl) {
          const colRect = colEl.getBoundingClientRect();
          if (colRect.left >= containerRect.left - 2) {
            visibleIdx = i;
            break;
          }
        }
      }

      // Compute target date by adding actual calendar days
      const visibleDate = dates[visibleIdx].date;
      const d = new Date(visibleDate + 'T12:00:00');
      d.setDate(d.getDate() + days);
      const targetDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      // Try to find the exact target date in loaded data
      const exactMatch = dates.findIndex((dt) => dt.date === targetDate);
      if (exactMatch >= 0) {
        scrollToDate(dates[exactMatch].date);
        return;
      }

      // Target is outside loaded range
      const firstLoadedDate = dates[0].date;
      const lastLoadedDate = dates[dates.length - 1].date;

      if (targetDate < firstLoadedDate) {
        // Target is before loaded range — load the month and scroll there
        await loadMonth(targetDate);
        // Wait for React to re-render with new columns
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Try exact target first, fall back to nearest loaded date
            const targetEl = colRefs.current.get(targetDate);
            if (targetEl) {
              scrollToDate(targetDate);
            } else {
              // Find nearest date after target in the now-updated refs
              for (const [dateKey] of colRefs.current) {
                if (dateKey >= targetDate) {
                  scrollToDate(dateKey);
                  break;
                }
              }
            }
          });
        });
      } else if (targetDate > lastLoadedDate) {
        // Target is after loaded range — load future and scroll to end for now
        await loadMonth(targetDate);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const targetEl = colRefs.current.get(targetDate);
            if (targetEl) {
              scrollToDate(targetDate);
            } else {
              scrollToDate(lastLoadedDate);
              loadMore('future');
            }
          });
        });
      } else {
        // Target is between loaded dates (e.g., skipped Sunday) — find nearest
        const nearestIdx = dates.findIndex((dt) => dt.date >= targetDate);
        if (nearestIdx >= 0) {
          scrollToDate(dates[nearestIdx].date);
        }
      }
    },
    [dates, scrollToDate, loadMonth, loadMore],
  );

  // Date picker handler
  const handleDatePick = useCallback(
    async (dateStr: string) => {
      if (!dateStr) return;
      // Check if date is already loaded
      const loaded = dates.some((d) => d.date === dateStr);
      if (loaded) {
        scrollToDate(dateStr);
      } else {
        // Load the month then scroll after React re-renders
        await loadMonth(dateStr);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToDate(dateStr);
          });
        });
      }
    },
    [dates, scrollToDate, loadMonth],
  );

  // Detect month boundaries for labeling
  const monthBoundaries = useMemo(() => {
    const boundaries = new Set<number>();
    for (let i = 1; i < dates.length; i++) {
      const prevMonth = dates[i - 1].date.substring(0, 7);
      const currMonth = dates[i].date.substring(0, 7);
      if (prevMonth !== currMonth) boundaries.add(i);
    }
    return boundaries;
  }, [dates]);

  return (
    <AppLayout title="Shift Planning">
      <div className="mx-auto max-w-[100rem] px-3 py-4 sm:px-4">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <h1 className="text-lg font-bold text-content-primary">Shift Planning</h1>
        </div>

        {/* Toolbar: Filters + Navigation + Actions */}
        <div className="mb-4 bg-surface-raised rounded-xl border border-edge-default p-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Battalion filter */}
            <select
              value={battalionFilter ?? ''}
              onChange={(e) => setBattalionFilter(e.target.value ? parseInt(e.target.value, 10) : null)}
              className="px-2.5 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
            >
              <option value="">All Battalions</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  Battalion {n}
                </option>
              ))}
            </select>

            {/* Unit type filter */}
            <select
              value={unitTypeFilter}
              onChange={(e) => setUnitTypeFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs bg-surface-base border border-edge-default rounded-lg text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
            >
              {UNIT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Divider */}
            <div className="mx-1 h-6 w-px bg-edge-muted" />

            {/* Date navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => jumpByDays(-30)}
                className="rounded-lg p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary transition-colors"
                title="Back one month"
              >
                <ChevronsLeft size={14} />
              </button>
              <button
                onClick={() => jumpByDays(-7)}
                className="rounded-lg p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary transition-colors"
                title="Back one week"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={scrollToToday}
                className="rounded-lg border border-edge-default bg-surface-base px-2.5 py-1 text-xs font-semibold text-content-primary hover:bg-surface-sunken transition-colors"
                title="Go to today"
              >
                Today
              </button>
              <button
                onClick={() => jumpByDays(7)}
                className="rounded-lg p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary transition-colors"
                title="Forward one week"
              >
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => jumpByDays(30)}
                className="rounded-lg p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary transition-colors"
                title="Forward one month"
              >
                <ChevronsRight size={14} />
              </button>
            </div>

            {/* Date picker */}
            <div className="relative flex items-center">
              <CalendarDays size={14} className="pointer-events-none absolute left-2 text-content-tertiary" />
              <input
                type="date"
                onChange={(e) => handleDatePick(e.target.value)}
                className="w-36 rounded-lg border border-edge-default bg-surface-base py-1.5 pl-7 pr-2 text-xs text-content-primary focus:outline-none focus:ring-1 focus:ring-action-primary"
                title="Jump to date"
              />
            </div>

            {/* Divider */}
            <div className="mx-1 h-6 w-px bg-edge-muted" />

            {/* Search */}
            <div className="relative flex items-center">
              <Search size={14} className="pointer-events-none absolute left-2.5 text-content-tertiary" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-32 rounded-lg border border-edge-default bg-surface-base py-1.5 pl-8 pr-7 text-xs text-content-primary placeholder:text-content-disabled focus:outline-none focus:ring-1 focus:ring-action-primary sm:w-48"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 text-content-tertiary hover:text-content-primary"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Refresh button */}
            <button
              onClick={refresh}
              className="rounded-lg border border-edge-default bg-surface-base p-1.5 text-content-tertiary hover:bg-surface-sunken hover:text-content-primary transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-content-tertiary" />
            <span className="ml-3 text-content-secondary">Loading shift data...</span>
          </div>
        )}

        {/* Grid */}
        {!loading && dates.length > 0 && (
          <div className="relative overflow-hidden rounded-xl border border-edge-default bg-surface-raised">
            <div className="flex">
              {/* Frozen left column */}
              <div className="w-48 shrink-0 border-r border-edge-default sm:w-56">
                {/* Corner cell */}
                <div className="h-[72px] border-b border-edge-muted bg-surface-sunken px-3 py-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-content-tertiary">Position</span>
                </div>
                {/* Row labels */}
                {visibleRows.map((row, idx) => {
                  if (row.type === 'station') {
                    const stats = stationStats.get(row.stationId);
                    const isCollapsed = collapsedStations.has(row.stationId);
                    return (
                      <div
                        key={`station-${row.stationId}-${idx}`}
                        className="flex h-8 cursor-pointer items-center border-t border-edge-default bg-surface-sunken px-2"
                        onClick={() => toggleStation(row.stationId)}
                      >
                        {isCollapsed ? (
                          <ChevronRight size={12} className="mr-1 shrink-0 text-content-tertiary" />
                        ) : (
                          <ChevronDown size={12} className="mr-1 shrink-0 text-content-tertiary" />
                        )}
                        <span className="truncate text-xs font-bold uppercase tracking-wider text-content-primary">
                          {row.stationLabel}
                        </span>
                        {stats && (
                          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[10px]">
                            <span className="text-emerald-400">{stats.filled}</span>
                            <span className="text-content-disabled">/</span>
                            <span className={stats.unfilled > 0 ? 'text-red-400' : 'text-content-disabled'}>
                              {stats.unfilled}
                            </span>
                          </span>
                        )}
                      </div>
                    );
                  }
                  if (row.type === 'unit') {
                    const unitKey = `${row.stationId}:${row.unitId}`;
                    const isCollapsed = collapsedUnits.has(unitKey);
                    return (
                      <div
                        key={`unit-${row.unitId}-${idx}`}
                        className="flex h-7 cursor-pointer items-center border-t border-edge-muted px-3"
                        onClick={() => toggleUnit(unitKey)}
                      >
                        {isCollapsed ? (
                          <ChevronRight size={10} className="mr-1 shrink-0 text-content-disabled" />
                        ) : (
                          <ChevronDown size={10} className="mr-1 shrink-0 text-content-disabled" />
                        )}
                        <span className="text-[11px] font-semibold text-content-secondary">{row.unitLabel}</span>
                      </div>
                    );
                  }
                  return (
                    <div key={`seat-${row.unitId}-${row.seatId}-${idx}`} className="flex h-10 items-center px-3">
                      <span
                        className={`truncate text-xs ${row.required ? 'font-semibold text-content-secondary' : 'text-content-tertiary'}`}
                      >
                        {row.seatLabel}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Scrollable date columns with scroll indicators */}
              <div className="relative flex-1 min-w-0">
                {/* Left scroll indicator */}
                {canScrollLeft && (
                  <button
                    onClick={() => scrollByAmount('left')}
                    className="absolute left-0 top-0 z-10 flex h-full w-8 items-center justify-center bg-gradient-to-r from-surface-raised to-transparent cursor-pointer"
                    aria-label="Scroll left"
                  >
                    <ChevronLeft size={16} className="text-content-secondary" />
                  </button>
                )}

                {/* Right scroll indicator */}
                {canScrollRight && (
                  <button
                    onClick={() => scrollByAmount('right')}
                    className="absolute right-0 top-0 z-10 flex h-full w-8 items-center justify-center bg-gradient-to-l from-surface-raised to-transparent cursor-pointer"
                    aria-label="Scroll right"
                  >
                    <ChevronRight size={16} className="text-content-secondary" />
                  </button>
                )}

                <div ref={scrollRef} className="overflow-x-auto" onScroll={handleScroll}>
                  <div className="inline-flex min-w-full">
                    {/* Loading past indicator */}
                    {loadingPast && (
                      <div className="flex w-12 shrink-0 items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-content-disabled" />
                      </div>
                    )}

                    {dates.map((day, colIdx) => {
                      const stats = columnStats[colIdx];
                      const bnColor = BN_COLORS[day.battalion_number];
                      const today = isToday(day.date);
                      const isMonthStart = monthBoundaries.has(colIdx);

                      return (
                        <div
                          key={day.date}
                          ref={(el) => {
                            if (el) colRefs.current.set(day.date, el);
                          }}
                          className={`w-20 shrink-0 sm:w-24 ${isMonthStart ? 'border-l-2 border-l-edge-default' : 'border-r border-r-edge-muted'}`}
                          style={today ? { backgroundColor: 'rgba(59, 130, 246, 0.06)' } : undefined}
                        >
                          {/* Column header */}
                          <div className="flex h-[72px] flex-col items-center justify-center border-b border-edge-muted px-1">
                            <span className="text-[10px] text-content-tertiary">{formatDayOfWeek(day.date)}</span>
                            <span className={`text-xs font-bold ${today ? 'text-blue-400' : 'text-content-primary'}`}>
                              {formatColDate(day.date)}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span
                                className="rounded px-1 py-0.5 text-[10px] font-bold"
                                style={
                                  bnColor
                                    ? {
                                        color: bnColor.text,
                                        backgroundColor: bnColor.bg,
                                        border: `1px solid ${bnColor.border}`,
                                      }
                                    : undefined
                                }
                              >
                                B{day.battalion_number}
                              </span>
                              <span
                                className={`text-[10px] font-medium ${stats.pct >= 90 ? 'text-emerald-400' : stats.pct >= 70 ? 'text-amber-400' : 'text-red-400'}`}
                              >
                                {stats.pct}%
                              </span>
                              {stats.unfilled > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] text-red-400">
                                  <AlertTriangle size={9} />
                                  {stats.unfilled}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Data cells */}
                          {visibleRows.map((row, rowIdx) => {
                            if (row.type === 'station') {
                              const isCollapsed = collapsedStations.has(row.stationId);
                              const dateStat = stationDateStats.get(`${day.date}:${row.stationId}`);
                              return (
                                <div
                                  key={`s-${row.stationId}-${rowIdx}`}
                                  className="flex h-8 items-center justify-center border-t border-edge-default bg-surface-sunken"
                                >
                                  {isCollapsed && dateStat && (
                                    <span
                                      className={`text-[10px] font-bold ${dateStat.filled >= dateStat.total ? 'text-emerald-400' : dateStat.filled > 0 ? 'text-amber-400' : 'text-red-400'}`}
                                    >
                                      {dateStat.filled}/{dateStat.total}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            if (row.type === 'unit') {
                              const unitKey = `${row.stationId}:${row.unitId}`;
                              const isCollapsed = collapsedUnits.has(unitKey);
                              const actualUnitId = resolveUnitId(row, day.battalion_number);
                              const dateStat = unitDateStats.get(`${day.date}:${actualUnitId}`);
                              return (
                                <div
                                  key={`u-${row.unitId}-${rowIdx}`}
                                  className="flex h-7 items-center justify-center border-t border-edge-muted"
                                >
                                  {isCollapsed && dateStat && (
                                    <span
                                      className={`text-[10px] font-medium ${dateStat.filled >= dateStat.total ? 'text-emerald-400' : dateStat.filled > 0 ? 'text-amber-400' : 'text-red-400'}`}
                                    >
                                      {dateStat.filled}/{dateStat.total}
                                    </span>
                                  )}
                                </div>
                              );
                            }

                            const actualUnitId = resolveUnitId(row, day.battalion_number);
                            const key = `${day.date}:${actualUnitId}:${row.seatId}`;
                            const assignment = assignmentLookup.get(key);
                            const hasCoverage = coverageLookup.has(`${day.date}:${row.seatId}`);
                            const memberId = assignment?.member_id ?? null;
                            const displayName = assignment?.display_name ?? null;
                            const isOverride = assignment?.is_override ?? false;
                            const photoUrl = memberId ? photos.get(memberId) : undefined;
                            const isEmpty = memberId === null;
                            const isRequired = row.required ?? false;

                            return (
                              <div
                                key={`c-${row.unitId}-${row.seatId}-${rowIdx}`}
                                className={`relative flex h-10 items-center justify-center ${
                                  isEmpty && isOfficer ? 'cursor-pointer hover:bg-surface-sunken' : ''
                                }`}
                                onClick={
                                  isEmpty && isOfficer
                                    ? () =>
                                        handleCellClick(
                                          day.date,
                                          day.battalion_id,
                                          actualUnitId,
                                          row.seatId!,
                                          row.seatLabel!,
                                        )
                                    : undefined
                                }
                                title={
                                  isEmpty
                                    ? isOfficer
                                      ? 'Click to assign'
                                      : 'Vacant'
                                    : `${displayName}${isOverride ? ' (override)' : ''}`
                                }
                              >
                                {memberId !== null ? (
                                  // Assigned cell
                                  <div className="relative">
                                    {photoUrl ? (
                                      <img
                                        src={photoUrl}
                                        alt=""
                                        className="h-7 w-7 rounded-full object-cover ring-1 ring-edge-default"
                                      />
                                    ) : (
                                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-sunken text-[9px] font-medium text-content-secondary ring-1 ring-edge-default">
                                        {displayName
                                          ?.split(' ')
                                          .map((w) => w[0])
                                          .join('')
                                          .slice(0, 2)
                                          .toUpperCase()}
                                      </div>
                                    )}
                                    {/* Amber overlay for active coverage request */}
                                    {hasCoverage && (
                                      <div className="absolute inset-0 rounded-full bg-amber-500/30 ring-2 ring-amber-500/50" />
                                    )}
                                    {/* Override indicator */}
                                    {isOverride && (
                                      <div className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-blue-400" />
                                    )}
                                  </div>
                                ) : isRequired ? (
                                  // Vacant required seat
                                  <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-dashed border-red-400/40 text-red-400/60">
                                    {isOfficer ? <UserPlus size={12} /> : <span className="text-[9px]">V</span>}
                                  </div>
                                ) : (
                                  // Vacant optional seat
                                  <div className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-edge-muted text-content-disabled">
                                    {isOfficer ? <UserPlus size={10} /> : null}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    {/* Loading future indicator */}
                    {loadingFuture && (
                      <div className="flex w-12 shrink-0 items-center justify-center">
                        <Loader2 size={16} className="animate-spin text-content-disabled" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && dates.length === 0 && !error && (
          <div className="py-16 text-center text-content-tertiary">
            <p className="text-lg">No scheduled shifts found.</p>
            <p className="mt-1 text-sm">The schedule may not have been generated yet.</p>
          </div>
        )}

        {/* Assign Modal */}
        {assignTarget && (
          <AssignModal
            date={assignTarget.date}
            battalionId={assignTarget.battalionId}
            unitId={assignTarget.unitId}
            seatId={assignTarget.seatId}
            seatLabel={assignTarget.seatLabel}
            onAssign={assignMember}
            onClose={() => setAssignTarget(null)}
          />
        )}
      </div>
    </AppLayout>
  );
}
